import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import AddIcon from "@mui/icons-material/Add";
import { useState, useMemo } from "react";
import SwitchToggle from "../../components/SwitchToggle";
import React from "react";
import AddCategorySubcategory from "../../Modals/addCategorySubcategory";
import { useGetCategories, useDeleteCategory, useUpdateCategory } from "../../api/hooks/useCategory";
import { showSuccess, showError } from "../../helpers/toast";
import type { Category } from "../../api/types/category.types";
import { CATEGORY_TYPE } from "../../api/types/category.types";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import Pagination from "../../components/Panigation";

const ManageCategories = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // Items per page

  // Debounce search term (500ms delay)
  React.useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1); // Reset to page 1 when search changes
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedCategory(null);
  };

  // Fetch categories (type: 1) - always call API, include search param only when searching
  const { data: categoriesData, isLoading: isLoadingCategories, refetch: refetchCategories, error: categoriesError } = useGetCategories(
    {
      ...(debouncedSearchTerm.trim() && { search: debouncedSearchTerm.trim() }), // Only include search if there's a search term
      page,
      limit,
      offset: (page - 1) * limit,
    },
    { enabled: true } // Always enable query
  );

  // Debug: Log API response
  React.useEffect(() => {
    if (categoriesData) {
      console.log('Categories API Response:', categoriesData);
    }
    if (categoriesError) {
      console.error('Categories API Error:', categoriesError);
      showError((categoriesError as any)?.response?.data?.message || 'Failed to load categories');
    }
  }, [categoriesData, categoriesError]);

  // Extract categories and totalCount from response
  const { categories, totalCount } = useMemo(() => {
    const data = categoriesData?.data;
    let cats: Category[] = [];
    let total = 0;
    
    if (Array.isArray(data)) {
      cats = data;
      total = data.length;
    } else if (data && typeof data === 'object') {
      const dataObj = data as any;
      if ('list' in dataObj && Array.isArray(dataObj.list)) {
        cats = dataObj.list;
        total = dataObj.totalCount || dataObj.list.length;
      } else if ('data' in dataObj && Array.isArray(dataObj.data)) {
        cats = dataObj.data;
        total = dataObj.totalCount || dataObj.data.length;
      } else if (dataObj.totalCount !== undefined) {
        total = dataObj.totalCount;
      }
    }
    
    if (!Array.isArray(cats)) {
      cats = [];
    }
    
    return {
      categories: cats,
      totalCount: total || cats.length,
    };
  }, [categoriesData]);

  // Use categories directly (no client-side filtering since we use server-side search)
  const filteredCategories = categories;

  // Delete category mutation
  const { mutate: deleteCategory, isPending: isDeleting } = useDeleteCategory({
    onSuccess: (response) => {
      showSuccess(response.message || "Category deleted successfully");
      refetchCategories();
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to delete category");
    },
  });

  // Update category status mutation
  const { mutate: updateCategoryStatus, isPending: isUpdatingStatus } = useUpdateCategory({
    onSuccess: (response) => {
      showSuccess(response.message || "Status updated successfully");
      refetchCategories();
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to update status");
    },
  });

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  // Handle edit category
  const handleEditCategory = (category: Category) => {
    setSelectedCategory(category);
    setOpenModal(true);
  };

  // Handle delete category
  const handleDeleteCategory = (id: string) => {
    setCategoryToDelete(id);
    setDeleteDialogOpen(true);
  };

  // Confirm delete category
  const handleConfirmDelete = () => {
    if (categoryToDelete) {
      deleteCategory(categoryToDelete);
    }
  };

  // Cancel delete category
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  // Handle add new category
  const handleAddCategory = () => {
    setSelectedCategory(null);
    setOpenModal(true);
  };

  // Handle status toggle
  const handleStatusToggle = (category: Category) => (event: React.ChangeEvent<HTMLInputElement>) => {
    updateCategoryStatus({
      id: category._id,
      data: {
        status: event.target.checked,
      },
    });
  };

  // Get subcategories count (we'll fetch this separately for each category)
  // For now, we'll show a placeholder or fetch on demand
  const getSubcategoriesCount = (categoryId: string) => {
    // This would ideally be fetched, but for now we'll return 0
    // In a real implementation, you might want to fetch subcategories for each category
    return 0;
  };

  return (
    <>
      <div className="main_title">
        <h1>Manage Categories</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Manage Categories
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search Category..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              onCross={() => {
                setSearchTerm("");
                setDebouncedSearchTerm("");
              }}
            />
          </div>

          <div className="right_s">
            <Button 
              className="btn btn_primary" 
              onClick={handleAddCategory}
              disabled={isLoadingCategories || isDeleting || isUpdatingStatus}
            >
              <AddIcon /> Add Category
            </Button>
          </div>
        </div>

        <TableContainer className="table_container">
          {isLoadingCategories ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress />
            </Box>
          ) : categoriesError ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <p>Error loading categories. Please try again.</p>
              <Button onClick={() => refetchCategories()} style={{ marginLeft: 10 }}>
                Retry
              </Button>
            </Box>
          ) : filteredCategories.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <p>
                {debouncedSearchTerm.trim() 
                  ? `No categories found for "${debouncedSearchTerm}"`
                  : "No categories found"}
              </p>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Sr.</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredCategories.map((category: Category, index: number) => (
                  <TableRow hover key={category._id}>
                    <TableCell>{(page - 1) * limit + index + 1}</TableCell>

                    <TableCell>
                      <div className="click_action user_block">
                        <p>
                          <b>{category.name}</b>
                        </p>
                      </div>
                    </TableCell>

                    <TableCell>{formatDate(category.createdAt)}</TableCell>

                    <TableCell>
                      <SwitchToggle
                        checked={category.status}
                        onChange={handleStatusToggle(category)}
                        disabled={isLoadingCategories || isDeleting || isUpdatingStatus}
                      />
                    </TableCell>

                    <TableCell>
                      <div className="table_actions v2">
                        <IconButton
                          onClick={() => handleEditCategory(category)}
                          disabled={isLoadingCategories || isDeleting || isUpdatingStatus}
                          title="Edit Category"
                        >
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>

                        <IconButton
                          onClick={() => handleDeleteCategory(category._id)}
                          disabled={isLoadingCategories || isDeleting || isUpdatingStatus}
                          title="Delete Category"
                        >
                          <img src="/static/images/trash2_icon.svg" alt="Delete" />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        
        {/* Pagination */}
        {!isLoadingCategories && !categoriesError && totalCount > 0 && (
          <Pagination
            module={filteredCategories}
            page={page}
            onPageChange={(newPage) => {
              setPage(newPage);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            totalPages={Math.ceil(totalCount / limit)}
            totalCount={totalCount}
            setPage={setPage}
            limit={limit}
          />
        )}
      </div>

      {/* Add/Edit Category Modal */}
      <AddCategorySubcategory
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        category={selectedCategory}
        type={CATEGORY_TYPE.CATEGORY}
        onSuccess={() => {
          refetchCategories();
        }}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Category"
        message="Are you sure you want to delete this category? This will also delete all associated subcategories. This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="error"
        isLoading={isDeleting}
        loadingText="Deleting..."
      />
    </>
  );
};

export default ManageCategories;
